import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';

export interface SeriesSummaryDto {
  id: string;
  name: string;
  description?: string | null;
  coverImageUrl?: string | null;
  bookCount: number;
  updatedAt: string;
}

export interface SeriesBookDto {
  id: string;
  bookId: string;
  bookTitle: string;
  sequenceOrder: number;
}

export interface SeriesDetailDto {
  id: string;
  name: string;
  description?: string | null;
  coverImageUrl?: string | null;
  books: SeriesBookDto[];
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

export function useMySeries() {
  const { getToken } = useAuth();
  return useQuery<SeriesSummaryDto[]>({
    queryKey: ['series'],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get('api/v2/series')
        .json<ApiEnvelope<SeriesSummaryDto[]>>();
      return resp.data ?? [];
    },
  });
}

export function useSeriesById(id: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<SeriesDetailDto>({
    enabled: !!id,
    queryKey: ['series', id],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get(`api/v2/series/${id}`)
        .json<ApiEnvelope<SeriesDetailDto>>();
      return resp.data;
    },
  });
}

export interface CreateSeriesInput {
  name: string;
  description?: string;
  coverImageUrl?: string;
}

export function useCreateSeries() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateSeriesInput) => {
      const client = createApiClient(getToken);
      const resp = await client
        .post('api/v2/series', { json: input })
        .json<ApiEnvelope<SeriesSummaryDto>>();
      return resp.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['series'] }),
  });
}

export interface UpdateSeriesInput {
  name: string;
  description?: string | null;
  coverImageUrl?: string | null;
}

export function useUpdateSeries(id: string | undefined) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateSeriesInput) => {
      if (!id) throw new Error('Series id required');
      const client = createApiClient(getToken);
      const resp = await client
        .put(`api/v2/series/${id}`, { json: input })
        .json<ApiEnvelope<SeriesSummaryDto>>();
      return resp.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['series'] });
      void qc.invalidateQueries({ queryKey: ['series', id] });
    },
  });
}

export function useDeleteSeries() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/series/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['series'] }),
  });
}

export function useAddBookToSeries(seriesId: string | undefined) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (bookId: string) => {
      if (!seriesId) throw new Error('Series id required');
      const client = createApiClient(getToken);
      const resp = await client
        .post(`api/v2/series/${seriesId}/books/${bookId}`)
        .json<ApiEnvelope<SeriesBookDto>>();
      return resp.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['series', seriesId] });
      void qc.invalidateQueries({ queryKey: ['series'] });
    },
  });
}

export function useRemoveBookFromSeries(seriesId: string | undefined) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (bookId: string) => {
      if (!seriesId) throw new Error('Series id required');
      const client = createApiClient(getToken);
      await client.delete(`api/v2/series/${seriesId}/books/${bookId}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['series', seriesId] });
      void qc.invalidateQueries({ queryKey: ['series'] });
    },
  });
}
